import React, { useState, useEffect } from 'react';
import { throttle } from 'lodash';

import { useCursorContext } from '$contexts/cursor.jsx';
import { CloseIcon, FullscreenIcon, ShopIcon } from '$partials/icon';
import { Button } from '$partials/ui';

const DEFAULT_CURSOR_TYPE = 'default';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Cursor
 * @props (see propTypes)
 *

 * This cursor is displayed all the time by default, or you can registerAreas to only display the cursor in those.
 * It has a focused state when hovering on interactiveElement, which can be conbined with the mode state.
 *
 * 3. Tracks the interactive elements and focus the cursor if the mouse
 *    is over one of them.
 * 3.1. Detects when the active element has been left due to page reflows or
 *     programatic display manipulations.
 * 4. If the cursor is displayed tracks the position of the mouse
 *    and replicates on the custom cursor.
 * 5. Tracks each area registered in the context and display the cursor only
 *    in those areas.
 * 6. Default mode that displays the cursor on all the page.
 */
const Cursor = () => {
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [cursorType, setCursorType] = useState(DEFAULT_CURSOR_TYPE);
    const [isCursorFocused, setIsCursorFocused] = useState(false);
    const [isCursorHidden, setIsCursorHidden] = useState(true);
    const { state } = useCursorContext();

    const interactiveElementSelector = 'a, button, input, textarea';

    const sensorDetect = (e) => {
        // Prevent cursor display on load
        if (isCursorHidden) {
            setIsCursorHidden(false);
        }

        setCursorPosition({ x: e.clientX, y: e.clientY });

        /* [3] */
        const isInteractiveElement = e.target.matches(interactiveElementSelector);
        if (isInteractiveElement) {
            setIsCursorFocused(true);

            e.target.addEventListener('mouseleave', () => {
                setIsCursorFocused(false);
            });

            e.target.addEventListener('click', e => {
                setTimeout(() => {
                    const hasLeft = !e.target.matches('*:hover');
                    hasLeft && setIsCursorFocused(false); /* [3.1] */
                }, 50);
            });
        }
    };

    /* [4] */
    useEffect(() => {
        if (!isCursorHidden) { return; }
        const syncCursorPosition = throttle(e => setCursorPosition({ x: e.clientX, y: e.clientY }), 0);
        addEventListener('mousemove', syncCursorPosition);
        return () => removeEventListener('mousemove', syncCursorPosition);
    }, []);

    useEffect(() => {
        let cleanups = [];

        /* [5] */
        if (state.areas.length) {
            cleanups = state.areas.map(({
                ref,
                cursorType = DEFAULT_CURSOR_TYPE
            }) => {
                const setCursorConfiguration = () => {
                    setIsCursorHidden(false);
                    setCursorType(cursorType);
                };

                const clearCursorConfiguration = () => {
                    setCursorType(DEFAULT_CURSOR_TYPE);
                };

                ref.current?.addEventListener('mouseenter', setCursorConfiguration);
                ref.current?.addEventListener('mouseleave', clearCursorConfiguration);
                ref.current?.addEventListener('mouseover', sensorDetect);

                return () => {
                    setCursorType(DEFAULT_CURSOR_TYPE);
                    ref.current?.removeEventListener('mouseenter', setCursorConfiguration);
                    ref.current?.removeEventListener('mouseleave', clearCursorConfiguration);
                    ref.current?.removeEventListener('mouseover', sensorDetect);
                };
            });
        }
        addEventListener('mouseover', sensorDetect);

        return () => {
            cleanups.forEach(cleanup => cleanup());
            removeEventListener('mouseover', sensorDetect);
        };
    }, [ state.areas ]);

    switch (cursorType) {
        case 'expand':
            return (
                <div
                    aria-hidden='true'
                    className='z5 cursor-full-screen'
                    style={{ transform: `translate(${cursorPosition.x}px, ${cursorPosition.y}px) translate(-50%, -50%)` }}>
                    <Button
                        as = 'button'
                        className = 'bg-white p3 md-p2'
                        look = 'button'
                        renderIcon = {<FullscreenIcon/>}/>
                </div>
            );
        case 'exit':
            return (
                <div
                    aria-hidden='true'
                    className='z5 cursor-full-screen'
                    style={{ transform: `translate(${cursorPosition.x}px, ${cursorPosition.y}px) translate(-50%, -50%)` }}>
                    <Button
                        as = 'button'
                        className = 'bg-white p3 md-p5'
                        look = 'button'
                        renderIcon = {<CloseIcon/>}/>
                </div>
            );
        case 'default':
        default:
            return (
                <></>
            );
    }
};



/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Cursor.propTypes = {};



export default Cursor;
